/**
 * CashForecastChart — 13-week cash forecast with confidence bands.
 * Recharts AreaChart: confidence band (shaded), projected line, min threshold.
 */

import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from 'recharts'
import { cn } from '../../lib/utils'
import { CHART_COLORS } from '../../config/chartTheme'

interface CashWeek {
  week: number
  starting: number
  inflows: number
  outflows: number
  ending: number
  confidence_low: number
  confidence_high: number
}

interface CashForecastChartProps {
  forecast: CashWeek[]
  minCashThreshold?: number
  className?: string
}

function formatDollar(v: number): string {
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toFixed(0)}`
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload as CashWeek | undefined
  if (!d) return null

  return (
    <div className={cn(
      'rounded-lg border border-white/10 bg-[var(--bg-elevated,var(--bg-elevated))]',
      'px-3 py-2 shadow-lg text-[11px]',
    )}>
      <p className="font-semibold text-[var(--text-primary,#F0F2F5)] mb-1">Week {d.week}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[var(--text-secondary,#94A3B8)]">
        <span>Starting</span><span className="text-right font-mono">{formatDollar(d.starting)}</span>
        <span>Inflows</span><span className="text-right font-mono text-emerald-400">{formatDollar(d.inflows)}</span>
        <span>Outflows</span><span className="text-right font-mono text-red-400">{formatDollar(d.outflows)}</span>
        <span className="font-semibold text-[var(--text-primary,#F0F2F5)]">Ending</span>
        <span className="text-right font-mono font-semibold text-[var(--text-primary,#F0F2F5)]">{formatDollar(d.ending)}</span>
      </div>
      <div className="mt-1 pt-1 border-t border-white/5 text-[9px] text-[var(--text-hint,#475569)]">
        Range: {formatDollar(d.confidence_low)} - {formatDollar(d.confidence_high)}
      </div>
    </div>
  )
}

export function CashForecastChart({ forecast, minCashThreshold = 2_000_000, className }: CashForecastChartProps) {
  if (!forecast.length) {
    return (
      <div className={cn('flex items-center justify-center py-8', className)}>
        <p className="text-[12px] text-[var(--text-muted,#64748B)]">No forecast data</p>
      </div>
    )
  }

  // Transform data for stacked area (confidence band = low to high)
  const chartData = forecast.map(w => ({
    ...w,
    name: `W${w.week}`,
    band_low: w.confidence_low,
    band_range: w.confidence_high - w.confidence_low,
  }))

  return (
    <div className={className}>
      <h4 className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-3 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
        13-Week Cash Forecast
      </h4>
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
        'px-3 pt-4 pb-2',
      )}>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" />
            <XAxis
              dataKey="name"
              tick={{ fill: CHART_COLORS.textMuted, fontSize: 10 }}
              axisLine={{ stroke: 'rgba(0,0,0,0.06)' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatDollar}
              tick={{ fill: CHART_COLORS.textMuted, fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Confidence band — hidden low base + visible range */}
            <Area
              type="monotone"
              dataKey="band_low"
              stackId="band"
              stroke="none"
              fill="transparent"
            />
            <Area
              type="monotone"
              dataKey="band_range"
              stackId="band"
              stroke="none"
              fill={CHART_COLORS.accent}
              fillOpacity={0.08}
            />

            {/* Projected line */}
            <Area
              type="monotone"
              dataKey="ending"
              stroke={CHART_COLORS.accent}
              strokeWidth={2}
              fill={CHART_COLORS.accent}
              fillOpacity={0.05}
              dot={{ r: 3, fill: CHART_COLORS.accent, stroke: CHART_COLORS.bgSurface, strokeWidth: 2 }}
            />

            {/* Min cash threshold */}
            <ReferenceLine
              y={minCashThreshold}
              stroke={CHART_COLORS.negative}
              strokeDasharray="6 3"
              strokeWidth={1}
              label={{
                value: `Min ${formatDollar(minCashThreshold)}`,
                position: 'insideTopRight',
                fill: CHART_COLORS.negative,
                fontSize: 9,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
